import { View, ViewProps } from 'react-native';
import { cva, type VariantProps } from 'class-variance-authority';
import { forwardRef } from 'react';
import { cn } from '@/utils/cn';

const boxVariants = cva('', {
  variants: {
    p: {
      0: 'p-0',
      1: 'p-1',
      2: 'p-2',
      3: 'p-3',
      4: 'p-4',
      5: 'p-5',
      6: 'p-6',
      8: 'p-8',
      10: 'p-10',
      12: 'p-12',
      16: 'p-16',
      20: 'p-20',
      24: 'p-24',
    },
    px: {
      0: 'px-0',
      1: 'px-1',
      2: 'px-2',
      3: 'px-3',
      4: 'px-4',
      5: 'px-5',
      6: 'px-6',
      8: 'px-8',
      10: 'px-10',
      12: 'px-12',
      16: 'px-16',
      20: 'px-20',
      24: 'px-24',
    },
    py: {
      0: 'py-0',
      1: 'py-1',
      2: 'py-2',
      3: 'py-3',
      4: 'py-4',
      5: 'py-5',
      6: 'py-6',
      8: 'py-8',
      10: 'py-10',
      12: 'py-12',
      16: 'py-16',
      20: 'py-20',
      24: 'py-24',
    },
    m: {
      0: 'm-0',
      1: 'm-1',
      2: 'm-2',
      3: 'm-3',
      4: 'm-4',
      5: 'm-5',
      6: 'm-6',
      8: 'm-8',
      10: 'm-10',
      12: 'm-12',
      16: 'm-16',
      20: 'm-20',
      24: 'm-24',
      auto: 'm-auto',
    },
    mx: {
      0: 'mx-0',
      1: 'mx-1',
      2: 'mx-2',
      3: 'mx-3',
      4: 'mx-4',
      5: 'mx-5',
      6: 'mx-6',
      8: 'mx-8',
      10: 'mx-10',
      12: 'mx-12',
      16: 'mx-16',
      20: 'mx-20',
      24: 'mx-24',
      auto: 'mx-auto',
    },
    my: {
      0: 'my-0',
      1: 'my-1',
      2: 'my-2',
      3: 'my-3',
      4: 'my-4',
      5: 'my-5',
      6: 'my-6',
      8: 'my-8',
      10: 'my-10',
      12: 'my-12',
      16: 'my-16',
      20: 'my-20',
      24: 'my-24',
      auto: 'my-auto',
    },
    bg: {
      transparent: 'bg-transparent',
      primary: 'bg-surface-primary',
      secondary: 'bg-surface-secondary',
      tertiary: 'bg-surface-tertiary',
      overlay: 'bg-surface-overlay',
      elevated: 'bg-surface-elevated',
      code: 'bg-surface-code',
    },
    border: {
      none: '',
      default: 'border border-border',
      light: 'border border-border-light',
      dark: 'border border-border-dark',
      primary: 'border border-border-primary',
      muted: 'border border-border-muted',
    },
    rounded: {
      none: 'rounded-none',
      xs: 'rounded-xs',
      sm: 'rounded-sm',
      md: 'rounded-md',
      lg: 'rounded-lg',
      xl: 'rounded-xl',
      '2xl': 'rounded-2xl',
      '3xl': 'rounded-3xl',
      full: 'rounded-full',
    },
    shadow: {
      none: 'shadow-none',
      xs: 'shadow-xs',
      sm: 'shadow-sm',
      md: 'shadow-md',
      lg: 'shadow-lg',
      xl: 'shadow-xl',
      '2xl': 'shadow-2xl',
    },
    position: {
      relative: 'relative',
      absolute: 'absolute',
      fixed: 'fixed',
      sticky: 'sticky',
    },
    flex: {
      1: 'flex-1',
      none: 'flex-none',
      auto: 'flex-auto',
    },
    direction: {
      row: 'flex-row',
      'row-reverse': 'flex-row-reverse',
      col: 'flex-col',
      'col-reverse': 'flex-col-reverse',
    },
    justify: {
      start: 'justify-start',
      end: 'justify-end',
      center: 'justify-center',
      between: 'justify-between',
      around: 'justify-around',
      evenly: 'justify-evenly',
    },
    align: {
      start: 'items-start',
      end: 'items-end',
      center: 'items-center',
      baseline: 'items-baseline',
      stretch: 'items-stretch',
    },
    wrap: {
      nowrap: 'flex-nowrap',
      wrap: 'flex-wrap',
      'wrap-reverse': 'flex-wrap-reverse',
    },
    hover: {
      none: '',
      subtle: 'hover:bg-surface-elevated/20 transition-colors duration-200',
      medium: 'hover:bg-surface-elevated/50 transition-colors duration-200',
      strong: 'hover:bg-surface-elevated transition-colors duration-200',
    },
  },
  defaultVariants: {},
});

export interface BoxProps extends ViewProps, VariantProps<typeof boxVariants> {
  className?: string;
  children?: React.ReactNode;
}

const Box = forwardRef<View, BoxProps>(
  (
    {
      className,
      p,
      px,
      py,
      m,
      mx,
      my,
      bg,
      border,
      rounded,
      shadow,
      position,
      flex,
      direction,
      justify,
      align,
      wrap,
      hover,
      ...props
    },
    ref
  ) => {
    return (
      <View
        ref={ref}
        className={cn(
          boxVariants({
            p,
            px,
            py,
            m,
            mx,
            my,
            bg,
            border,
            rounded,
            shadow,
            position,
            flex,
            direction,
            justify,
            align,
            wrap,
            hover,
          }),
          className
        )}
        {...props}
      />
    );
  }
);

Box.displayName = 'Box';

export { Box, boxVariants };
